import {useAuthUser, useAuthClientId} from 'components/organisms/Providers/AuthProvider/AuthProvider';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectEvent, useEffectItem} from 'helpers/hooks/utils';
import utils from 'helpers/utils';
import {useTable} from 'components/organisms/Providers/TableProvider/TableProvider';
import StyledEntitiesViewBar from 'components/organisms/Bars/EntitiesViewBar/EntitiesViewBar.styles';
import {useCommentGet} from 'services/comment/comment.hooks';
import {useCommentCreate, useTaskResponsibility} from 'services/comment/comment.utils';
import Save from '@mui/icons-material/Save';
import IosShare from '@mui/icons-material/IosShare';
import ConfirmDialog from 'components/organisms/Dialogs/ConfirmDialog/ConfirmDialog';
import {useLocation, useParams} from 'react-router-dom';
import {linkTo, useLinkNavigate} from 'helpers/hooks/links';
import constants from 'helpers/constants';
import {useSnackbar} from 'components/organisms/Providers/SnackbarProvider/SnackbarProvider';
import {useDialogControl} from 'components/organisms/Providers/DialogProvider/DialogProvider';
import ShareDialog from 'components/organisms/Dialogs/ShareDialog/ShareDialog';
import {useClientInsightGet} from 'services/client/insight/insight.hooks';

const EntitiesViewBar = React.forwardRef((props, ref) => {
  const {
    collection,
    ...innerProps
  } = useComponentProps({
    color: 'primary',
    ...props
  }, 'EntitiesViewBar');

  const refetchRef = useRef(null);
  const [internalState, setInternalState] = useState({
    submitting: false,
    showMoreDialog: false,
    showFinishDialog: false
  });

  const tableProvider = useTable();

  const user = useAuthUser();
  const clientId = useAuthClientId();

  const location = useLocation();
  const navigate = useLinkNavigate();
  const snackbar = useSnackbar();
  const dialogControl = useDialogControl();

  const commentCreate = useCommentCreate();
  const updateViewEvent = useEffectEvent(tableProvider?.updaters?.updateView);

  const view = tableProvider?.view;
  const { entityId } = useParams();

  const insightView = view?.activeViews?.find((v) => v.name === 'insight');
  const hasInsightView = Boolean(insightView);
  const insightId = hasInsightView ? +insightView.params?.[0] : null;
  const insight = useClientInsightGet({
    clientId: clientId,
    insightId: insightId
  }, { enabled: hasInsightView && insightId > 0 });

  const taskView = view?.activeViews?.find((v) => v.name === 'task');
  const hasTaskView = Boolean(taskView);
  const taskId = hasTaskView ? +taskView.params?.[1] : null;
  const activeView = hasTaskView ? taskView.params?.slice(-1)[0] === 'active' : null;
  const task = useCommentGet({
    commentId: taskId
  }, { enabled: hasTaskView && taskId > 0 });

  const [isResponsible,, openTasks, totalTasks] = useTaskResponsibility(task.data, user?.userId);
  const done = !tableProvider.list?.data?.find((d) => {
    return openTasks?.find((ot) => +ot.relationId === +d.entityId || +ot.relationId === +d.originalEntityId)
  });

  const actionableView = hasTaskView ? taskView : null;
  const closableView = hasTaskView ? taskView : null;
  const loaded = !tableProvider.isLoading() &&
    !tableProvider.list?.status?.isReloading &&
    utils.isDefined(tableProvider.list?.data) &&
    (!hasTaskView || (task?.data && task?.status?.isSuccess && !task?.status?.isLoading));

  const setViewDataEvent = useEffectEvent(tableProvider.setViewData);
  useEffect(() => {
    if (tableProvider.listState?.active) {
      let data;
      if (hasTaskView && taskId > 0 && task.data) {
        data = data ?? {};
        data.task = task.data;
      }
      if (hasInsightView && insightId > 0 && insight.data) {
        data = data ?? {};
        data.insight = insight.data;
      }

      if (data) {
        setViewDataEvent?.(data);
      }
    }
  }, [tableProvider.listState?.active, hasInsightView, hasTaskView, insight.data, insightId, setViewDataEvent, task.data, taskId]);

  const resetEvent = useEffectEvent(tableProvider.reset);
  const refetchEvent = useEffectEvent(tableProvider.list?.query?.refetch);
  useEffect(() => {
    if (utils.isDefined(refetchRef.current)) {
      if (hasTaskView && refetchRef.current !== entityId) {
        refetchEvent?.();
        resetEvent?.();
      }
    }
    refetchRef.current = entityId ?? -1;
  }, [hasTaskView, entityId, refetchEvent, resetEvent]);

  const debouncedDialog = useMemo(() => {
    return utils.debounce((activeView, hasTaskView, loaded, openTasks, done) => {
      let showingDialog = false;

      if (activeView) {
        if (hasTaskView && openTasks && loaded) {
          if (openTasks?.length === 0) {
            showingDialog = true;
            setInternalState(utils.updater({
              showMoreDialog: false,
              showFinishDialog: true
            }, true));
          } else if (done) {
            showingDialog = true;
            setInternalState(utils.updater({
              showMoreDialog: true,
              showFinishDialog: false
            }, true));
          }
        }
      }

      if (!showingDialog) {
        setInternalState(utils.updater({
          showMoreDialog: false,
          showFinishDialog: false
        }, true));
      }
    }, constants.debounce.short);
  }, []);

  useEffect(() => {
    debouncedDialog(activeView, hasTaskView, loaded, openTasks, done);
  }, [activeView, hasTaskView, loaded, openTasks, done, debouncedDialog]);

  const handleConfirmClose = (e, reason) => {
    setInternalState(utils.updater({
      showMoreDialog: false,
      showFinishDialog: false
    }, true));

    if (reason !== 'confirmButtonClick') {
      if (closableView) {
        const path = utils.removePathParams(location.pathname + location.search, `custom=${closableView.name}:${closableView.params.slice(0, -1).join('_')}_active`);
        navigate({
          to: `${path}${path.includes('?') ? '&' : '?'}custom=${closableView.name}:${closableView.params.slice(0, -1).join('_')}_closed`,
          keepSearchParams: false,
          replace: true
        });
      }
    }
  }

  const handleConfirmMore = () => {
    setInternalState(utils.updater({
      showMoreDialog: false,
      showFinishDialog: false
    }, true));

    let path = `${location.pathname}?custom=${actionableView.name}`
    if (closableView) {
      path = `${location.pathname}?custom=${closableView.name}:${closableView.params.slice(0, -1).join('_')}_active`;
    }

    navigate({
      to: path,
      keepSearchParams: false,
      replace: true
    });
  }

  const handleConfirmFinish = () => {
    setInternalState(utils.updater({
      showMoreDialog: false,
      showFinishDialog: false
    }, true));

    navigate({to: location.pathname, replace: true, resetSearchParams: false});
  }

  const viewSettings = useEffectItem(tableProvider?.viewSettings?.());
  const actions = useMemo(() => {
    const actions = [];

    if (view?.userView && view?.userView?.viewId > 0) {
      const hasChanges = utils.viewChanged(viewSettings, view?.userView?.settings);

      actions.push({
        label: hasChanges ? 'Save view' : 'Share view',
        icon: hasChanges ? Save : IosShare,
        auth: hasChanges ? utils.createAuth({attribute: 'view.update', meta: {view: view?.userView}}) :
          utils.createAuth({attribute: 'view.share', meta: {view: view?.userView}}),
        onClick: () => {
          if (hasChanges) {
            setInternalState(utils.updater({submitting: true}, true));
            updateViewEvent?.(view?.userView, {settings: viewSettings})
              .catch(() => {
                snackbar.show('Saving view failed', null,
                  {color: 'error', autoHideDuration: constants.delay.error});
              })
              .finally(() => {
                setInternalState(utils.updater({submitting: false}, true));
              });
          } else {
            const handleSubmit = (data) => {
              const comment = data.comment;
              const recipients = data.recipients;

              const subscriptions = utils.clone(view?.userView?.subscriptions, true);
              recipients.forEach((recipient) => {
                if (!subscriptions.find((subscription) => +subscription.userId === +recipient.value)) {
                  subscriptions.push({
                    userId: +recipient.value
                  })
                }
              })

              return updateViewEvent?.(view?.userView, {subscriptions: subscriptions})
                .then(() => {
                  return commentCreate(comment, recipients, collection?.collectionId);
                });
            }

            const link = linkTo({to: `?view=${view?.userView?.viewId}`, relative: 'path'}, location, true);
            const comment = `**${user?.firstName}** has shared this view with you [${view?.userView?.name}](${link})`
            dialogControl.show(<ShareDialog type="view"
                                            comment={comment}
                                            onSubmit={handleSubmit} />, true);
          }
        },
        ButtonProps: {
          size: 'small',
          variant: 'text',
          radius: 'square',
          disabled: internalState.submitting
        }
      });
    }

    return actions;
  }, [view?.userView, user?.firstName, internalState.submitting, location, dialogControl,
    snackbar, viewSettings, collection?.collectionId, commentCreate, updateViewEvent]);

  if (activeView || isResponsible) {
    innerProps.progress = innerProps.progress ?? (!hasTaskView ? null : (
        !((totalTasks?.length ?? 0) > 0) ? 0 :
          (((totalTasks?.length ?? 0) - openTasks?.length)) / (totalTasks?.length ?? 0) * 100)
    );
    innerProps.progressText = innerProps.progressText ?? (
      hasTaskView ? `${(totalTasks?.length ?? 0) - (openTasks?.length ?? 0)} / ${totalTasks?.length ?? 0}` : null
    );

    innerProps.progressTooltip = innerProps.progressTooltip ?? (
      hasTaskView ? 'Task progress' : null
    )
  }

  innerProps.isLoading = innerProps.isLoading || tableProvider.isLoading() || task.status.isLoading;

  innerProps.className = utils.flattenClassName(innerProps.className);

  return <StyledEntitiesViewBar ref={ref} {...innerProps} actions={actions}>
    <ConfirmDialog question="You have finished the tasks for the selected companies, do you want to finish the task for the rest of the companies?"
                   onClose={handleConfirmClose}
                   onConfirm={handleConfirmMore}
                   open={internalState.showMoreDialog}
                   ConfirmButtonProps={{
                     children: 'Continue',
                     color: 'primary'
                   }}/>
    <ConfirmDialog question="You have finished the task for all the companies, do you want to close the view?"
                   onClose={handleConfirmClose}
                   onConfirm={handleConfirmFinish}
                   open={internalState.showFinishDialog}
                   ConfirmButtonProps={{
                     children: 'Close view',
                     color: 'success'
                   }}/>
    {innerProps.children}
  </StyledEntitiesViewBar>
});

EntitiesViewBar.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  collection: PropTypes.object
};

export default EntitiesViewBar;
